// Intent pages — authority cluster, part 1.
// Every section leads with a concise, extractable direct answer (AI-search ready),
// followed by supporting depth. Claims use substantiation-safe language.

export const CORE_PAGES = {
  "digital-twin": {
    slug: "digital-twin",
    metaTitle: "Human Digital Twin Health Platform | Integrated Wellness",
    metaDescription:
      "What is a health digital twin? How Integrated Wellness builds a continuously updated model of your biology from labs, DNA, microbiome, wearables and biometrics — reviewed by licensed clinicians.",
    eyebrow: "Platform Authority",
    h1: "The Human Digital Twin Health Platform",
    isMedical: false,
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/b2ledv8q_image_2.png",
    imageAlt: "Integrated human systems model combining anatomy, biometrics and connected technology",
    directAnswer:
      "A health digital twin is a continuously updated digital representation of your biology, built from laboratory results, DNA, microbiome data, wearable signals, biometrics and health history. Integrated Wellness interprets these inputs together through the patent-pending Integrated Matrix Engine — and every recommendation is reviewed by licensed medical professionals before it reaches you.",
    sections: [
      {
        q: "What is a health digital twin?",
        a: "A health digital twin is a living model of your body built from real measurements — labs, genetics, microbiome, wearable data and biometrics — that updates as new data arrives. Unlike a static report, it is designed to reflect how your biology is trending right now.",
        detail:
          "Recent healthcare literature describes digital twins as multimodal representations that combine molecular, clinical, behavioral and sensor information. That is exactly the direction Integrated Wellness has engineered: one unified model rather than five disconnected dashboards.",
      },
      {
        q: "What data does a health digital twin use?",
        a: "Our Digital Twin fuses six input streams: laboratory panels, DNA analysis, microbiome sequencing, continuous wearable signals (HRV, sleep, recovery, glucose), body-composition biometrics, and your structured health history.",
        detail:
          "Each stream is useful on its own — but the meaningful patterns usually live between them. A normal lab value can carry a very different meaning when your genetics, gut signature and recovery trends are read alongside it.",
      },
      {
        q: "How does the Digital Twin change over time?",
        a: "Every retest, wearable sync and check-in refreshes the model. The platform follows a Measure → Learn → Adjust loop: your protocol is measured against outcomes, the model learns from the response, and your clinician adjusts the plan.",
        detail:
          "This longitudinal context is what separates a digital twin from a one-off assessment. Trends, rates of change and divergence between data domains are often more informative than any single snapshot.",
      },
      {
        q: "How are recommendations generated?",
        a: "The Integrated Matrix Engine evaluates patterns across your data domains — pattern recognition, multi-domain analysis, safety routing, evidence evaluation and longitudinal context. It is designed to surface findings and draft protocol options, not to make medical decisions.",
        detail:
          "Every engine output is packaged for clinician review. The technology supports clinician interpretation; it does not replace it.",
      },
      {
        q: "Where does AI stop and clinician review begin?",
        a: "AI stops at analysis. The engine organizes your data, surfaces patterns and flags safety considerations. Licensed medical professionals review every finding, decide what is clinically appropriate, and approve your protocol before anything is recommended to you.",
        detail:
          "Medical services are delivered by Integrated Wellness Medical Center under the clinical direction of Dr. Michael Ellis, DO. Wellness coaching services are non-medical and clearly separated from clinical care.",
      },
      {
        q: "How is your privacy protected?",
        a: "Your data is encrypted in transit and at rest, access is role-restricted to your care team, and information is never sold. Security architecture is overseen by enterprise IT leadership with two decades of experience in regulated, data-sensitive environments.",
        detail: null,
      },
      {
        q: "How is this different from a wearable dashboard?",
        a: "A wearable dashboard displays one device's data. A digital twin interprets many data domains together, places them in longitudinal context, and routes the result through clinician review — turning raw numbers into a supervised, personalized protocol.",
        detail:
          "Dashboards tell you what happened. A digital twin is designed to help you and your clinician decide what to do next.",
      },
    ],
    measures: ["Lab Work", "DNA", "Microbiome", "Wearables", "Biometrics", "Health History"],
    faqs: [
      {
        q: "Do I need a wearable to build a Digital Twin?",
        a: "No. Wearables enrich the model with continuous signals, but a Digital Twin can start from labs, history and biometrics alone. You can connect Apple Health, Oura, WHOOP, Fitbit or Garmin at any time.",
      },
      {
        q: "Is the Digital Twin a medical device?",
        a: "No. The Digital Twin platform is clinical decision-support and wellness technology. It is designed to support clinician interpretation — diagnosis and treatment decisions are always made by licensed medical professionals.",
      },
      {
        q: "How often does my Digital Twin update?",
        a: "Wearable signals stream continuously; labs and biometrics refresh at retest intervals set by your clinician — typically quarterly for active programs.",
      },
      {
        q: "Can my existing doctor use my Digital Twin data?",
        a: "Yes. Reports are structured so any physician can review them. Our principle is simple: we are not here to replace doctors — we empower them.",
      },
    ],
    related: [
      { label: "Precision Medicine", href: "/precision-medicine" },
      { label: "Biological Age Testing", href: "/biological-age-testing" },
      { label: "Wearable Health Data", href: "/wearable-health-data" },
      { label: "Wearable Integrations", href: "/wearable-integrations" },
    ],
  },

  "precision-medicine": {
    slug: "precision-medicine",
    metaTitle: "Precision Medicine & Telehealth | Integrated Wellness",
    metaDescription:
      "What is precision medicine? How Integrated Wellness combines advanced labs, DNA, microbiome and wearable data with the Integrated Matrix Engine and physician oversight to personalize care nationwide.",
    eyebrow: "Precision Medicine",
    h1: "Precision Medicine, Built Around Your Biology",
    isMedical: true,
    image: "https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/662942ca7518cb626bc2d2b2d4a68bc9acbc3069949471836852bb3eebb158a2.jpeg",
    imageAlt: "Clinician reviewing a personalized health plan with a patient in a modern medical setting",
    about: "Precision Medicine",
    directAnswer:
      "Precision medicine tailors prevention, diagnostics and treatment to your individual biology instead of population averages. Integrated Wellness delivers it by combining advanced laboratory testing, DNA, microbiome and wearable data through the patent-pending Integrated Matrix Engine — with every protocol reviewed and prescribed by licensed physicians via nationwide telemedicine.",
    sections: [
      {
        q: "What is precision medicine?",
        a: "Precision medicine is an approach that uses your genetics, biomarkers, lifestyle and environment to personalize medical decisions. Instead of asking \u201cwhat works for most people,\u201d it asks \u201cwhat is most likely to work for you.\u201d",
        detail:
          "Reference ranges describe populations. Your optimal ranges depend on your genetics, hormone profile, metabolic state and history — which is why two people with identical labs can need very different plans.",
      },
      {
        q: "How is precision medicine different from functional medicine?",
        a: "Functional medicine looks for root causes across body systems; precision medicine adds quantitative personalization — genetics, multi-omic data and longitudinal measurement. Integrated Wellness practices both: root-cause thinking, powered by multi-domain data and clinician oversight.",
        detail: null,
      },
      {
        q: "What testing is involved?",
        a: "Advanced blood panels (hormonal, metabolic, inflammatory, cardiovascular), DNA analysis including pharmacogenomics, microbiome sequencing, body-composition biometrics and continuous wearable data. Your clinician selects the panel that matches your goals and history.",
        detail:
          "Testing is staged — a comprehensive baseline first, then targeted retesting to measure protocol response rather than re-running everything blindly.",
      },
      {
        q: "How does the Integrated Matrix Engine contribute?",
        a: "The engine reads all data domains together and is designed to surface patterns a single-panel review may miss — for example, borderline labs that become meaningful in the context of genetic predisposition and declining recovery trends. Findings are routed to your physician for interpretation.",
        detail: null,
      },
      {
        q: "Is precision medicine available by telehealth?",
        a: "Yes. Integrated Wellness Medical Center delivers physician consultations, lab ordering, protocol prescriptions and follow-up nationwide via telemedicine, with labs drawn locally near you.",
        detail: null,
      },
      {
        q: "Who is precision medicine for?",
        a: "People with unresolved symptoms despite \u201cnormal\u201d labs, those optimizing longevity or performance, anyone starting hormone or metabolic therapy, and patients who want their care built on measurement rather than guesswork.",
        detail: null,
      },
    ],
    measures: [
      "Advanced Blood Panels",
      "DNA & Pharmacogenomics",
      "Microbiome Sequencing",
      "Body Composition",
      "Wearable Signals",
      "Hormone & Metabolic Axes",
    ],
    faqs: [
      {
        q: "Do I need a referral?",
        a: "No referral is needed. Start with a free health assessment and our clinical team will route you to the right pathway.",
      },
      {
        q: "Does insurance cover precision medicine?",
        a: "Programs are typically direct-pay. Some labs may be billable to insurance depending on your plan; our team will clarify before anything is ordered.",
      },
      {
        q: "Which states do you serve?",
        a: "Medical services are delivered by licensed clinicians via telemedicine across the United States, with in-person options in the New York metro area.",
      },
      {
        q: "How fast do I get results?",
        a: "Most baseline panels return within 1–2 weeks. Your physician reviews the Matrix Engine synthesis with you in a scheduled consult.",
      },
    ],
    related: [
      { label: "Digital Twin Platform", href: "/digital-twin" },
      { label: "Advanced Blood Testing", href: "/advanced-blood-testing" },
      { label: "Medical Care", href: "/medical-care" },
      { label: "Functional Medicine", href: "/functional-medicine" },
      { label: "Genetic Testing", href: "/genetic-testing" },
    ],
  },

  "longevity-medicine": {
    slug: "longevity-medicine",
    metaTitle: "Longevity Medicine & Healthspan Optimization | Integrated Wellness",
    metaDescription:
      "What is longevity medicine? Evidence-informed healthspan optimization — biological age testing, metabolic and hormone data, and physician-supervised protocols from Integrated Wellness.",
    eyebrow: "Longevity Medicine",
    h1: "Longevity Medicine & Healthspan Optimization",
    isMedical: true,
    image: "https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/4a2835904aaea6343a5b9f1a0c31ef3ff5d548c190786de0bfdc711f5ed9b484.jpeg",
    imageAlt: "Active older adults building mobility, strength and healthy longevity through guided exercise",
    about: "Longevity Medicine",
    directAnswer:
      "Longevity medicine focuses on extending healthspan — the years you live in good function — by measuring and addressing the biology of aging before disease develops. Integrated Wellness combines biological age testing, metabolic, hormonal and inflammatory biomarkers with physician-supervised protocols designed to support healthy aging over years, not weeks.",
    sections: [
      {
        q: "What is longevity medicine?",
        a: "Longevity medicine is proactive, measurement-driven care aimed at slowing functional decline and extending healthspan. It works upstream of diagnosis — evaluating patterns associated with aging, such as metabolic drift, hormonal decline, inflammation and loss of physiological reserve.",
        detail:
          "The goal is not simply more years — it is more years of strength, cognition, mobility and independence.",
      },
      {
        q: "What is the difference between lifespan and healthspan?",
        a: "Lifespan is how long you live; healthspan is how long you live well. The last decade of life often carries the highest burden of disease — longevity medicine is designed to compress that period by intervening decades earlier.",
        detail: null,
      },
      {
        q: "What do we measure in a longevity program?",
        a: "Biological age across epigenetic, vascular and behavioral pillars, inflammatory and glycation markers, hormone and thyroid axes, insulin sensitivity, body composition and VO2-related fitness measures, plus continuous recovery data from wearables.",
        detail:
          "Our patent-pending Integrated Biological Age 2.0 engine weights epigenetic, vascular and behavioral signals into one score and is designed to detect divergence patterns between them — see our dedicated biological age page for the full methodology.",
      },
      {
        q: "What does a longevity protocol include?",
        a: "Nutrition and training targets built from your data, sleep and recovery engineering, targeted supplementation, and — where clinically appropriate and physician-prescribed — hormone or metabolic therapies. Each element is tracked and retested.",
        detail:
          "For emerging interventions, we grade the supporting evidence and clearly distinguish established medicine from promising-but-early science.",
      },
      {
        q: "How is progress tracked?",
        a: "Through retesting on a clinician-set cadence: biological age markers, labs and body composition quarterly to annually, with wearable trends monitored continuously between visits. Protocols are adjusted based on measured response.",
        detail: null,
      },
    ],
    measures: [
      "Biological Age (3 Pillars)",
      "Inflammation & Glycation",
      "Hormone & Thyroid Axes",
      "Insulin Sensitivity",
      "Body Composition",
      "Recovery & Fitness Trends",
    ],
    faqs: [
      {
        q: "At what age should longevity work start?",
        a: "The biology of aging is measurable by your 30s, and earlier baselines make trends more informative. Most of our members start between 35 and 60 — but the best time is when you have data worth acting on, which is now.",
      },
      {
        q: "Can biological age be improved?",
        a: "Published research associates lifestyle, metabolic and cardiovascular improvements with favorable movement in aging biomarkers. Individual results vary — which is exactly why we measure yours rather than promise a number.",
      },
      {
        q: "Is this medical care or wellness coaching?",
        a: "Both pathways exist. Prescription therapies run through Integrated Wellness Medical Center under physician oversight; lifestyle-only longevity coaching is available as a non-medical wellness service.",
      },
      {
        q: "How is this different from a longevity supplement stack?",
        a: "Supplements without measurement are guesses. Our programs are built on baseline testing, clinician review and scheduled retesting — supplementation is one tool inside a measured system.",
      },
    ],
    related: [
      { label: "Biological Age Testing", href: "/biological-age-testing" },
      { label: "Healthspan vs Lifespan", href: "/healthspan" },
      { label: "Sauna Therapy", href: "/sauna-therapy" },
      { label: "Longevity Optimization Program", href: "/programs/longevity-optimization" },
      { label: "VO2 Max Optimization", href: "/vo2-max-optimization" },
    ],
  },

  "biological-age-testing": {
    slug: "biological-age-testing",
    metaTitle: "Biological Age Testing: Epigenetic, Vascular & Behavioral | Integrated Wellness",
    metaDescription:
      "What is biological age testing? How multi-modal testing — 30+ methylation clocks, AI vascular age, behavioral scoring — measures how you're actually aging. Physician-reviewed at Integrated Wellness.",
    eyebrow: "Biological Age",
    h1: "Biological Age Testing",
    isMedical: true,
    image: "https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/e04343809871ad79f91be68f718fb2037e2f1518daf4f37f06c38c3d7d9fffbc.jpeg",
    imageAlt: "Older adult demonstrating strength and mobility as functional aging markers",
    about: "Biological Age Assessment",
    directAnswer:
      "Biological age testing estimates how old your body functions compared with your calendar age, using measurable biology such as DNA methylation, vascular signals and behavioral reserve. Integrated Wellness runs a patent-pending multi-modal evaluation — Integrated Biological Age 2.0 — that weights epigenetic (40%), vascular (35%) and behavioral (25%) pillars into one physician-reviewed score.",
    sections: [
      {
        q: "What is biological age?",
        a: "Biological age reflects the measurable condition of your cells, vessels and physiological reserve — how you are actually aging — as opposed to chronological age, which only counts years. Two 45-year-olds can differ by more than a decade biologically.",
        detail: null,
      },
      {
        q: "How is biological age different from chronological age?",
        a: "Chronological age is fixed by your birthday. Biological age is dynamic: it responds to metabolic health, cardiovascular function, sleep, training, stress and environment — which means it can be measured, tracked and acted on.",
        detail: null,
      },
      {
        q: "What do methylation clocks measure — and what are their limits?",
        a: "Methylation clocks read chemical patterns on your DNA that shift with age. They are powerful but incomplete: recent research challenges methylation-only assessment because molecular age can diverge from vascular function and real-world physiological reserve.",
        detail:
          "That is why our engine runs 30+ methylation clocks in parallel for the epigenetic pillar — and then reconciles them against two additional, independent pillars instead of reporting a single-signal number.",
      },
      {
        q: "How does Integrated Biological Age 2.0 work?",
        a: "Three pillars are weighted into one score: epigenetic (30+ methylation clocks, 40%), vascular (an AI engine reading PPG resonance for vascular age, 35%) and behavioral (our BAPS-v3 score across 8 behavioral domains, 25%). The engine is designed to detect six divergence patterns between pillars.",
        detail:
          "If pillars disagree — for example, young molecules but low behavioral reserve — the engine flags a Fragile-aging pattern and gates higher-tier interventions until the underlying gap is addressed. This Biological Resilience Reserve concept separates Resilient from Fragile aging.",
      },
      {
        q: "What happens after the test?",
        a: "Your results are synthesized by the Matrix Engine and reviewed with a clinician. You receive a pillar-by-pillar breakdown, divergence findings where present, and a protocol targeting your weakest pillar first — then retesting to measure response.",
        detail: null,
      },
    ],
    measures: [
      "30+ Methylation Clocks",
      "AI Vascular Age (PPG)",
      "BAPS-v3 Behavioral Score",
      "Divergence Patterns",
      "Resilience Reserve",
      "Longitudinal Trend",
    ],
    faqs: [
      {
        q: "Is one biological age test enough?",
        a: "A single test is a baseline. The real value is the trend — retesting after 6–12 months of protocol work shows whether your biology is responding.",
      },
      {
        q: "Why not just take a cheap methylation test?",
        a: "Single-clock tests can mislead: methylation age can look favorable while vascular function or physiological reserve tells a different story. Multi-modal testing is designed to surface those divergences.",
      },
      {
        q: "Is biological age testing a diagnosis?",
        a: "No. Biological age is an assessment tool that supports clinical decision-making — it does not diagnose disease. All findings are reviewed by licensed clinicians.",
      },
      {
        q: "How do I start?",
        a: "Begin with the free health assessment. Our team will confirm eligibility, arrange testing, and schedule your clinician review.",
      },
    ],
    related: [
      { label: "IBA 2.0 Technology Deep-Dive", href: "/biological-age" },
      { label: "Metabolic Age", href: "/metabolic-age" },
      { label: "Longevity Medicine", href: "/longevity-medicine" },
      { label: "Digital Twin Platform", href: "/digital-twin" },
    ],
  },
};
